import {useMemo} from 'react'

import {useGetDocumentFormatsQuery} from '@apis/aiCoreAPI'
import type {DocumentFormat} from '@interfaces/alpinist/document'

/**
 * What can be uploaded, from the backend (AB#5986).
 *
 * This used to be two hard-coded lists — one for the Knowledge Base upload, one
 * for the chat composer — and both drifted from the backend the first time a
 * format was added: a file type the server accepted was silently missing from
 * the file picker, and one it had stopped accepting was still offered.
 *
 * While the request is in flight `formats` is empty, so callers get "nothing is
 * accepted yet" rather than a stale guess. `isLoading` is there to keep the
 * picker disabled for that moment instead of showing an empty accept list.
 */
export const useDocumentFormats = () => {
  const {data, isLoading, isError} = useGetDocumentFormatsQuery()

  return useMemo(() => {
    const formats: DocumentFormat[] = data?.formats ?? []
    return {
      formats,
      maxAttachmentsPerMessage: data?.max_attachments_per_message ?? 0,
      conversationRetentionDays: data?.conversation_retention_days ?? 0,
      isLoading,
      isError,
      /** The `accept` attribute for a file input, and the drag-drop filter. */
      accept: formats.map((format) => format.extension).join(','),
    }
  }, [data, isLoading, isError])
}
